import React, { useState, useRef, Children } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { LinkedinIcon, GlobeIcon, AwardIcon, BriefcaseIcon, GraduationCapIcon, LeafIcon, UsersIcon, ArrowRightIcon, XIcon } from 'lucide-react';
import { Footer } from '../components/Footer';
const IVAN_PHOTO = 'https://cdn.magicpatterns.com/uploads/t49pLg5FC657EpgAgGe33M/image.png';
function AboutHero() {
  return <section className="relative w-full overflow-hidden bg-[#FAFDF7]" data-id="element-785">
      <svg className="absolute inset-0 w-full h-full opacity-[0.04]" aria-hidden="true" data-id="element-786">
        <defs data-id="element-787">
          <pattern id="about-grid" width="50" height="50" patternUnits="userSpaceOnUse" patternTransform="rotate(45)" data-id="element-788">
            <line x1="0" y1="0" x2="0" y2="50" stroke="#1B4332" strokeWidth="0.5" data-id="element-789" />
            <line x1="0" y1="0" x2="50" y2="0" stroke="#1B4332" strokeWidth="0.5" data-id="element-790" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#about-grid)" data-id="element-791" />
      </svg>

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-32" data-id="element-792">
        <motion.div className="text-center" initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-793">
          <p className="text-sm font-semibold uppercase tracking-widest text-carbon-bright mb-4" data-id="element-794">
            About CarbonSafe
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-carbon-deep tracking-tight leading-[1.1]" data-id="element-795">
            Technology-first climate
            <span className="text-carbon-bright" data-id="element-796"> partners</span>
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-carbon-mid max-w-2xl mx-auto leading-relaxed" data-id="element-797">
            We bridge the gap between complex digital systems and sustainable
            real-world applications, turning purpose into profit across Uganda's
            green economy.
          </p>
        </motion.div>
      </div>
    </section>;
}
function CEOSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const [showHighlights, setShowHighlights] = useState(false);
  return <section ref={ref} className="w-full bg-white py-20 sm:py-28 border-t border-b border-carbon-geo" data-id="element-798">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-799">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16" data-id="element-800">
          {/* Photo & Quick Info */}
          <motion.div className="lg:col-span-2" initial={{
          opacity: 0,
          x: -30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.7,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-801">
            <div className="sticky top-28" data-id="element-802">
              <div className="relative" data-id="element-803">
                <div className="w-full aspect-[4/5] rounded-2xl overflow-hidden bg-carbon-geo" data-id="element-804">
                  <img src={IVAN_PHOTO} alt="Ivan Kahangire, Founder & CEO of CarbonSafe" className="w-full h-full object-cover object-top" data-id="element-805" />
                </div>
                {/* Accent bar */}
                <div className="absolute -bottom-3 -right-3 w-24 h-24 rounded-xl bg-carbon-bright/10 -z-10" aria-hidden="true" data-id="element-806" />
                <div className="absolute -top-3 -left-3 w-16 h-16 rounded-lg bg-carbon-deep/5 -z-10" aria-hidden="true" data-id="element-807" />
              </div>

              <div className="mt-8" data-id="element-808">
                <h2 className="text-2xl font-extrabold text-carbon-deep" data-id="element-809">
                  Ivan Kahangire
                </h2>
                <p className="text-carbon-bright font-semibold mt-1" data-id="element-810">
                  Founder & CEO
                </p>

                <div className="mt-4 flex flex-wrap gap-2" data-id="element-811">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-carbon-geo bg-[#FAFDF7] text-xs font-medium text-carbon-deep" data-id="element-812">
                    <AwardIcon className="w-3 h-3 text-carbon-bright" data-id="element-813" />
                    ISO 14064 Certified
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-carbon-geo bg-[#FAFDF7] text-xs font-medium text-carbon-deep" data-id="element-814">
                    <UsersIcon className="w-3 h-3 text-carbon-bright" data-id="element-815" />
                    Founder Institute
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-carbon-geo bg-[#FAFDF7] text-xs font-medium text-carbon-deep" data-id="element-816">
                    <LeafIcon className="w-3 h-3 text-carbon-bright" data-id="element-817" />
                    Green Economy
                  </span>
                </div>

                <div className="mt-5 flex gap-3" data-id="element-818">
                  <a href="#" className="w-9 h-9 rounded-lg bg-carbon-deep/5 flex items-center justify-center text-carbon-mid hover:text-carbon-bright hover:bg-carbon-bright/10 transition-colors" aria-label="LinkedIn" data-id="element-819">
                    <LinkedinIcon className="w-4 h-4" data-id="element-820" />
                  </a>
                  <a href="#" className="w-9 h-9 rounded-lg bg-carbon-deep/5 flex items-center justify-center text-carbon-mid hover:text-carbon-bright hover:bg-carbon-bright/10 transition-colors" aria-label="Website" data-id="element-821">
                    <GlobeIcon className="w-4 h-4" data-id="element-822" />
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Bio Content */}
          <motion.div className="lg:col-span-3" initial={{
          opacity: 0,
          x: 30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.7,
          delay: 0.15,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-823">
            <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-carbon-bright bg-carbon-bright/10 px-3 py-1 rounded-full mb-5" data-id="element-824">
              Leadership
            </span>

            <blockquote className="text-xl sm:text-2xl font-bold text-carbon-deep leading-snug border-l-4 border-carbon-bright pl-5 mb-8" data-id="element-825">
              "Digital transformation should drive environmental resilience. We
              monetize purpose, turning sustainable ideas into scalable business
              ventures."
            </blockquote>

            <p className="text-base text-carbon-mid leading-relaxed mb-6" data-id="element-826">
              As the Founder and Team Lead of CarbonSafe, Ivan Kahangire's
              career represents a strategic evolution from enterprise technology
              and data science into the heart of Uganda's green economy. His
              professional journey is defined by his ability to bridge the gap
              between complex digital systems and sustainable real-world
              applications.
            </p>

            <p className="text-base text-carbon-mid leading-relaxed mb-8" data-id="element-827">
              Ivan's career is anchored in the belief that digital
              transformation should drive environmental resilience. His
              leadership at CarbonSafe reflects a "Value Addition" mindset,
              moving beyond raw data to create high-integrity, verifiable carbon
              assets.
            </p>

            {/* Career Highlights Link */}
            <button onClick={() => setShowHighlights(true)} className="inline-flex items-center gap-2 text-carbon-bright font-semibold text-base hover:text-carbon-light transition-colors group mb-10" data-id="element-828">
              <BriefcaseIcon className="w-4.5 h-4.5" data-id="element-829" />
              View Career Highlights
              <ArrowRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-830" />
            </button>

            {/* Closing quote */}
            <div className="p-6 rounded-2xl bg-carbon-deep/[0.03] border border-carbon-geo" data-id="element-831">
              <p className="text-sm text-carbon-mid leading-relaxed italic" data-id="element-832">
                "Ivan's career trajectory, from managing global tech accounts to
                empowering rural farmers, ensures that CarbonSafe remains both
                technically sophisticated and locally grounded."
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Career Highlights Modal */}
      <AnimatePresence data-id="element-833">
        {showHighlights && <>
            {/* Backdrop */}
            <motion.div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} onClick={() => setShowHighlights(false)} data-id="element-834" />

            {/* Modal */}
            <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 pointer-events-none" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-835">
              <motion.div className="relative w-full max-w-2xl max-h-[85vh] bg-white rounded-2xl shadow-2xl overflow-hidden pointer-events-auto" initial={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} transition={{
            duration: 0.25,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-836">
                {/* Modal Header */}
                <div className="sticky top-0 z-10 flex items-center justify-between px-6 sm:px-8 py-5 bg-white border-b border-carbon-geo" data-id="element-837">
                  <div data-id="element-838">
                    <h3 className="text-lg font-bold text-carbon-deep" data-id="element-839">
                      Career Highlights
                    </h3>
                    <p className="text-sm text-carbon-mid mt-0.5" data-id="element-840">
                      Ivan Kahangire, Founder & CEO
                    </p>
                  </div>
                  <button onClick={() => setShowHighlights(false)} className="w-9 h-9 rounded-lg bg-carbon-deep/5 flex items-center justify-center text-carbon-mid hover:text-carbon-deep hover:bg-carbon-deep/10 transition-colors" aria-label="Close" data-id="element-841">
                    <XIcon className="w-5 h-5" data-id="element-842" />
                  </button>
                </div>

                {/* Modal Content */}
                <div className="px-6 sm:px-8 py-6 overflow-y-auto max-h-[calc(85vh-80px)]" data-id="element-843">
                  <div className="space-y-6" data-id="element-844">
                    <div className="flex gap-4" data-id="element-845">
                      <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-carbon-bright/10 flex items-center justify-center mt-0.5" data-id="element-846">
                        <BriefcaseIcon className="w-5 h-5 text-carbon-bright" data-id="element-847" />
                      </div>
                      <div data-id="element-848">
                        <h4 className="text-sm font-bold text-carbon-deep" data-id="element-849">
                          Technology & Data Leadership
                        </h4>
                        <p className="text-sm text-carbon-mid leading-relaxed mt-1" data-id="element-850">
                          Established himself as a Digital Transformation
                          Specialist and Data Scientist. Served as Territory
                          Manager for Oracle in East Africa, General Manager at
                          Computech Ltd, and Country Manager at DataGroupIT,
                          mastering Cyber Security, Cloud Computing, and
                          Enterprise Infrastructure that now power CarbonSafe's
                          dMRV capabilities.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4" data-id="element-851">
                      <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-carbon-bright/10 flex items-center justify-center mt-0.5" data-id="element-852">
                        <LeafIcon className="w-5 h-5 text-carbon-bright" data-id="element-853" />
                      </div>
                      <div data-id="element-854">
                        <h4 className="text-sm font-bold text-carbon-deep" data-id="element-855">
                          The Founder Spirit
                        </h4>
                        <p className="text-sm text-carbon-mid leading-relaxed mt-1" data-id="element-856">
                          CEO of Kahangire Farm Ltd, where he applied early
                          "AgTech" principles to modern farming. This hands-on
                          experience in agricultural production provided the
                          foundation for CarbonSafe's focus on soil carbon and
                          smallholder farmer integration.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4" data-id="element-857">
                      <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-carbon-bright/10 flex items-center justify-center mt-0.5" data-id="element-858">
                        <UsersIcon className="w-5 h-5 text-carbon-bright" data-id="element-859" />
                      </div>
                      <div data-id="element-860">
                        <h4 className="text-sm font-bold text-carbon-deep" data-id="element-861">
                          Ecosystem Architect
                        </h4>
                        <p className="text-sm text-carbon-mid leading-relaxed mt-1" data-id="element-862">
                          Recognized as a prominent mentor in the Ugandan
                          startup scene, Ivan serves as Co-Director at the
                          Founder Institute Kampala, graduating new waves of
                          technology companies and championing those in the
                          Green and Circular Economy.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4" data-id="element-863">
                      <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-carbon-bright/10 flex items-center justify-center mt-0.5" data-id="element-864">
                        <GraduationCapIcon className="w-5 h-5 text-carbon-bright" data-id="element-865" />
                      </div>
                      <div data-id="element-866">
                        <h4 className="text-sm font-bold text-carbon-deep" data-id="element-867">
                          Academic Foundation
                        </h4>
                        <p className="text-sm text-carbon-mid leading-relaxed mt-1" data-id="element-868">
                          A graduate of Bangalore University (India) and
                          Coventry University (UK), Ivan's academic background
                          in ICT and Business has allowed him to navigate the
                          intersection of technical engineering and strategic
                          market expansion.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4" data-id="element-869">
                      <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-carbon-bright/10 flex items-center justify-center mt-0.5" data-id="element-870">
                        <AwardIcon className="w-5 h-5 text-carbon-bright" data-id="element-871" />
                      </div>
                      <div data-id="element-872">
                        <h4 className="text-sm font-bold text-carbon-deep" data-id="element-873">
                          ISO 14064 Certified
                        </h4>
                        <p className="text-sm text-carbon-mid leading-relaxed mt-1" data-id="element-874">
                          Certified in Green House Gases standards, ensuring
                          CarbonSafe's verification processes meet the highest
                          international benchmarks for carbon accounting and
                          reporting.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </>}
      </AnimatePresence>
    </section>;
}
function TeamSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 40
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  return <section ref={ref} className="w-full bg-[#FAFDF7] py-20 sm:py-28" data-id="element-875">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-876">
        <motion.div className="text-center mb-14" initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-877">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-carbon-deep tracking-tight" data-id="element-878">
            The team behind{' '}
            <span className="text-carbon-bright" data-id="element-879">CarbonSafe</span>
          </h2>
          <p className="mt-4 text-lg text-carbon-mid max-w-2xl mx-auto" data-id="element-880">
            A multidisciplinary team of engineers, scientists, and
            sustainability experts building Africa's most trusted carbon
            verification platform.
          </p>
        </motion.div>

        <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" initial="hidden" animate={isInView ? 'visible' : 'hidden'} variants={{
        hidden: {},
        visible: {
          transition: {
            staggerChildren: 0.1
          }
        }
      }} data-id="element-881">
          {/* Team Member 1 */}
          <motion.article className="rounded-2xl border border-carbon-geo bg-white overflow-hidden" variants={cardVariants} data-id="element-882">
            <div className="h-48 bg-gradient-to-br from-carbon-deep to-carbon-mid flex items-center justify-center" data-id="element-883">
              <span className="text-5xl font-extrabold text-white/20" data-id="element-884">AN</span>
            </div>
            <div className="p-6" data-id="element-885">
              <h3 className="text-lg font-bold text-carbon-deep" data-id="element-886">
                Amara Nakato
              </h3>
              <p className="text-sm font-semibold text-carbon-bright mt-0.5" data-id="element-887">
                Chief Technology Officer
              </p>
              <p className="text-sm text-carbon-mid leading-relaxed mt-3" data-id="element-888">
                Former cloud architect at AWS East Africa. Leads CarbonSafe's
                IoT infrastructure, satellite data pipelines, and dMRV platform
                engineering.
              </p>
              <div className="mt-4 flex gap-2" data-id="element-889">
                <a href="#" className="w-8 h-8 rounded-lg bg-carbon-deep/5 flex items-center justify-center text-carbon-mid hover:text-carbon-bright transition-colors" aria-label="LinkedIn" data-id="element-890">
                  <LinkedinIcon className="w-3.5 h-3.5" data-id="element-891" />
                </a>
              </div>
            </div>
          </motion.article>

          {/* Team Member 2 */}
          <motion.article className="rounded-2xl border border-carbon-geo bg-white overflow-hidden" variants={cardVariants} data-id="element-892">
            <div className="h-48 bg-gradient-to-br from-carbon-bright/80 to-carbon-mid flex items-center justify-center" data-id="element-893">
              <span className="text-5xl font-extrabold text-white/20" data-id="element-894">DM</span>
            </div>
            <div className="p-6" data-id="element-895">
              <h3 className="text-lg font-bold text-carbon-deep" data-id="element-896">
                Dr. David Mugisha
              </h3>
              <p className="text-sm font-semibold text-carbon-bright mt-0.5" data-id="element-897">
                Head of Data Science
              </p>
              <p className="text-sm text-carbon-mid leading-relaxed mt-3" data-id="element-898">
                PhD in Environmental Remote Sensing from Makerere University.
                Develops the machine learning models that power CarbonSafe's
                emission quantification algorithms.
              </p>
              <div className="mt-4 flex gap-2" data-id="element-899">
                <a href="#" className="w-8 h-8 rounded-lg bg-carbon-deep/5 flex items-center justify-center text-carbon-mid hover:text-carbon-bright transition-colors" aria-label="LinkedIn" data-id="element-900">
                  <LinkedinIcon className="w-3.5 h-3.5" data-id="element-901" />
                </a>
              </div>
            </div>
          </motion.article>

          {/* Team Member 3 */}
          <motion.article className="rounded-2xl border border-carbon-geo bg-white overflow-hidden" variants={cardVariants} data-id="element-902">
            <div className="h-48 bg-gradient-to-br from-carbon-deep/80 to-carbon-bright/60 flex items-center justify-center" data-id="element-903">
              <span className="text-5xl font-extrabold text-white/20" data-id="element-904">SK</span>
            </div>
            <div className="p-6" data-id="element-905">
              <h3 className="text-lg font-bold text-carbon-deep" data-id="element-906">
                Sarah Kyomuhendo
              </h3>
              <p className="text-sm font-semibold text-carbon-bright mt-0.5" data-id="element-907">
                Head of Operations
              </p>
              <p className="text-sm text-carbon-mid leading-relaxed mt-3" data-id="element-908">
                10+ years in supply chain management across East Africa.
                Oversees field operations, cookstove distribution networks, and
                farmer onboarding programs.
              </p>
              <div className="mt-4 flex gap-2" data-id="element-909">
                <a href="#" className="w-8 h-8 rounded-lg bg-carbon-deep/5 flex items-center justify-center text-carbon-mid hover:text-carbon-bright transition-colors" aria-label="LinkedIn" data-id="element-910">
                  <LinkedinIcon className="w-3.5 h-3.5" data-id="element-911" />
                </a>
              </div>
            </div>
          </motion.article>

          {/* Team Member 4 */}
          <motion.article className="rounded-2xl border border-carbon-geo bg-white overflow-hidden" variants={cardVariants} data-id="element-912">
            <div className="h-48 bg-gradient-to-br from-carbon-mid/70 to-carbon-deep flex items-center justify-center" data-id="element-913">
              <span className="text-5xl font-extrabold text-white/20" data-id="element-914">JO</span>
            </div>
            <div className="p-6" data-id="element-915">
              <h3 className="text-lg font-bold text-carbon-deep" data-id="element-916">
                James Okello
              </h3>
              <p className="text-sm font-semibold text-carbon-bright mt-0.5" data-id="element-917">
                Head of Partnerships
              </p>
              <p className="text-sm text-carbon-mid leading-relaxed mt-3" data-id="element-918">
                Former sustainability lead at Nespresso East Africa. Manages
                relationships with carbon credit buyers, NGOs, and government
                regulatory bodies.
              </p>
              <div className="mt-4 flex gap-2" data-id="element-919">
                <a href="#" className="w-8 h-8 rounded-lg bg-carbon-deep/5 flex items-center justify-center text-carbon-mid hover:text-carbon-bright transition-colors" aria-label="LinkedIn" data-id="element-920">
                  <LinkedinIcon className="w-3.5 h-3.5" data-id="element-921" />
                </a>
              </div>
            </div>
          </motion.article>

          {/* Team Member 5 */}
          <motion.article className="rounded-2xl border border-carbon-geo bg-white overflow-hidden" variants={cardVariants} data-id="element-922">
            <div className="h-48 bg-gradient-to-br from-carbon-bright/60 to-carbon-deep/80 flex items-center justify-center" data-id="element-923">
              <span className="text-5xl font-extrabold text-white/20" data-id="element-924">PN</span>
            </div>
            <div className="p-6" data-id="element-925">
              <h3 className="text-lg font-bold text-carbon-deep" data-id="element-926">
                Patricia Namuli
              </h3>
              <p className="text-sm font-semibold text-carbon-bright mt-0.5" data-id="element-927">
                Lead Platform Engineer
              </p>
              <p className="text-sm text-carbon-mid leading-relaxed mt-3" data-id="element-928">
                Full-stack engineer with expertise in geospatial systems. Builds
                the real-time dashboards and mobile tools that field teams rely
                on daily.
              </p>
              <div className="mt-4 flex gap-2" data-id="element-929">
                <a href="#" className="w-8 h-8 rounded-lg bg-carbon-deep/5 flex items-center justify-center text-carbon-mid hover:text-carbon-bright transition-colors" aria-label="LinkedIn" data-id="element-930">
                  <LinkedinIcon className="w-3.5 h-3.5" data-id="element-931" />
                </a>
              </div>
            </div>
          </motion.article>

          {/* Team Member 6 */}
          <motion.article className="rounded-2xl border border-carbon-geo bg-white overflow-hidden" variants={cardVariants} data-id="element-932">
            <div className="h-48 bg-gradient-to-br from-carbon-deep to-carbon-bright/50 flex items-center justify-center" data-id="element-933">
              <span className="text-5xl font-extrabold text-white/20" data-id="element-934">RB</span>
            </div>
            <div className="p-6" data-id="element-935">
              <h3 className="text-lg font-bold text-carbon-deep" data-id="element-936">
                Ruth Birungi
              </h3>
              <p className="text-sm font-semibold text-carbon-bright mt-0.5" data-id="element-937">
                Community & Impact Lead
              </p>
              <p className="text-sm text-carbon-mid leading-relaxed mt-3" data-id="element-938">
                Background in community development and gender-inclusive climate
                programs. Ensures CarbonSafe's projects deliver measurable
                social impact alongside carbon outcomes.
              </p>
              <div className="mt-4 flex gap-2" data-id="element-939">
                <a href="#" className="w-8 h-8 rounded-lg bg-carbon-deep/5 flex items-center justify-center text-carbon-mid hover:text-carbon-bright transition-colors" aria-label="LinkedIn" data-id="element-940">
                  <LinkedinIcon className="w-3.5 h-3.5" data-id="element-941" />
                </a>
              </div>
            </div>
          </motion.article>
        </motion.div>
      </div>
    </section>;
}
function AboutCTA() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <section ref={ref} className="w-full py-20 sm:py-28 bg-[#FAFDF7]" data-id="element-942">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-943">
        <motion.div className="relative overflow-hidden rounded-3xl px-8 py-16 sm:px-16 sm:py-20 text-center" style={{
        background: 'linear-gradient(135deg, #2D6A4F 0%, #1B4332 100%)'
      }} initial={{
        opacity: 0,
        y: 40
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-944">
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-10" style={{
          background: 'radial-gradient(circle, #52B788, transparent)'
        }} aria-hidden="true" data-id="element-945" />
          <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full opacity-10" style={{
          background: 'radial-gradient(circle, #6BCB77, transparent)'
        }} aria-hidden="true" data-id="element-946" />
          <h2 className="relative text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight max-w-3xl mx-auto" data-id="element-947">
            Join our mission to build
            <br data-id="element-948" />
            <span className="text-carbon-bright" data-id="element-949">Africa's carbon future</span>
          </h2>
          <p className="relative mt-5 text-lg text-white/70 max-w-xl mx-auto" data-id="element-950">
            We're always looking for passionate people who want to make a real
            impact on climate action in East Africa.
          </p>
          <div className="relative mt-10 flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-951">
            <a href="#" className="inline-flex items-center gap-2 px-8 py-4 bg-carbon-bright text-white text-base font-semibold rounded-lg hover:bg-carbon-light transition-colors shadow-lg shadow-black/20" data-id="element-952">
              View Open Roles
              <ArrowRightIcon className="w-4 h-4" data-id="element-953" />
            </a>
            <a href="#" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white text-base font-semibold rounded-lg hover:bg-white/10 transition-colors" data-id="element-954">
              Contact Us
            </a>
          </div>
        </motion.div>
      </div>
    </section>;
}
export function About() {
  return <>
      <AboutHero data-id="element-955" />
      <CEOSection data-id="element-956" />
      <TeamSection data-id="element-957" />
      <AboutCTA data-id="element-958" />
      <Footer data-id="element-959" />
    </>;
}